import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { Printer, ChevronRight } from 'lucide-react'
import { service } from '../lib/api/service'
import { entryToATA } from '../lib/api/transform'
import { useProjectConfig } from '../lib/appContext'
import type { Project, ATAView } from '../lib/api/types'
import { formatSEK, kvarAttFakturera, isEarned } from '../utils/calculations'
import StatusBadge from './StatusBadge'
import MissingChips from './MissingChips'
import JournalFilterBar from './JournalFilterBar'
import { EMPTY_JOURNAL_FILTERS, type JournalFilters } from '../utils/journalFilters'

const ataKvar = (a: ATAView): number =>
  kvarAttFakturera(a.belopp, a.journal?.faktureratBelopp)

/** True when the ÄTA passes every active filter (empty filter = pass-through). */
function matchesFilters(a: ATAView, f: JournalFilters): boolean {
  const q = f.search.toLowerCase()
  if (q && !(
    String(a.sortOrder).includes(q) ||
    a.rubrik.toLowerCase().includes(q) ||
    a.beskrivning.toLowerCase().includes(q)
  )) return false

  if (f.statuses.length > 0 && !f.statuses.includes(a.status)) return false
  if (f.onlyKvar && ataKvar(a) <= 0) return false

  const belopp = a.belopp ?? 0
  if (f.minBelopp !== '' && belopp < parseFloat(f.minBelopp)) return false
  if (f.maxBelopp !== '' && belopp > parseFloat(f.maxBelopp)) return false

  const skapad = a.skapad?.slice(0, 10) ?? ''
  if (f.fromDate && (!skapad || skapad < f.fromDate)) return false
  if (f.toDate && (!skapad || skapad > f.toDate)) return false

  return true
}

export default function JournalTable() {
  const navigate = useNavigate()
  const { statuses } = useProjectConfig()

  const [project, setProject] = useState<Project | null>(null)
  const [atas, setAtas]       = useState<ATAView[]>([])
  const [loading, setLoading] = useState(true)
  const [filters, setFilters] = useState<JournalFilters>(EMPTY_JOURNAL_FILTERS)

  useEffect(() => {
    Promise.all([service.getProject(), service.getAllEntries(), service.getSurcharges()])
      .then(([proj, entries, sc]) => {
        setProject(proj)
        setAtas(entries.map(e => entryToATA(e, sc)))
      })
      .finally(() => setLoading(false))
  }, [])

  if (loading) return <div className="loading"><div className="spinner" /></div>

  const filtered = atas.filter(a => matchesFilters(a, filters))

  const sum = (pick: (a: ATAView) => number) => filtered.reduce((s, a) => s + pick(a), 0)
  const totalPrissatt   = sum(a => a.belopp ?? 0)
  const totalUpparbetat = sum(a => (isEarned(a.status) ? a.belopp ?? 0 : 0))
  const totalFakturerat = sum(a => a.journal?.faktureratBelopp ?? 0)
  const totalKvar       = sum(ataKvar)

  const projectLine = project
    ? [project.projektnr, project.projektNamn, project.ort].filter(Boolean).join(' · ')
    : null

  return (
    <div>
      <div className="page-header">
        <div>
          {projectLine && <div className="page-eyebrow">{projectLine}</div>}
          <h1 className="page-title">Journal</h1>
          <div className="page-sub">
            Den interna journalen för platsledningen. Varje ÄTA är en post med rubrik,
            beskrivning och fakturahänvisning. Beställaren ser inte denna text.
          </div>
        </div>
        <div className="page-actions">
          <button className="icon-btn" onClick={() => navigate('/print/journal')}>
            <Printer size={14} />
            Skriv ut journal
          </button>
        </div>
      </div>

      <JournalFilterBar filters={filters} onChange={setFilters} statuses={statuses} />

      {atas.length === 0 ? (
        <div className="card">
          <div className="empty-state">
            <h3>Inga ÄTA registrerade</h3>
            <p>Klicka på "Ny ÄTA" för att skapa den första ändringsordran.</p>
          </div>
        </div>
      ) : (
        <div className="card" style={{ padding: 0 }}>
          {filtered.length === 0 && (
            <div style={{ padding: '24px', textAlign: 'center', color: 'var(--ink-faint)' }}>
              Inga ÄTA matchar filtren
            </div>
          )}
          {filtered.map((a, i) => (
            <JournalRow
              key={a.sortOrder}
              ata={a}
              last={i === filtered.length - 1}
              onClick={() => navigate(`/ata/${a.sortOrder}`)}
            />
          ))}
        </div>
      )}

      {atas.length > 0 && (
        <div className="journal-totals">
          <span style={{ color: 'var(--ink-faint)', fontSize: 12 }}>
            {filtered.length !== atas.length
              ? `${filtered.length} av ${atas.length} poster`
              : `${atas.length} poster totalt`}
          </span>
          <div style={{ flex: 1 }} />
          <TotalPair label="Prissatt"   value={totalPrissatt} />
          <TotalPair label="Upparbetat" value={totalUpparbetat} />
          <TotalPair label="Fakturerat" value={totalFakturerat} />
          <TotalPair label="Kvar att fakturera" value={totalKvar} emphasis />
        </div>
      )}
    </div>
  )
}

function TotalPair({ label, value, emphasis }: { label: string; value: number; emphasis?: boolean }) {
  return (
    <span style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', minWidth: 90 }}>
      <span style={{ fontSize: 10.5, textTransform: 'uppercase', letterSpacing: '.04em', color: 'var(--ink-faint)' }}>{label}</span>
      <span style={{ fontFamily: 'var(--mono)', fontSize: 13, fontWeight: 600, color: emphasis && value > 0 ? 'var(--accent)' : 'var(--ink)' }}>
        {formatSEK(value)}
      </span>
    </span>
  )
}

interface JournalRowProps {
  ata: ATAView
  last: boolean
  onClick: () => void
}

function JournalRow({ ata, last, onClick }: JournalRowProps) {
  const j = ata.journal
  const kvar = ataKvar(ata)
  const hasEconomy = ata.belopp != null || j?.faktureratBelopp != null

  return (
    <div
      className="journal-row"
      style={{ borderBottom: last && !hasEconomy ? 'none' : undefined }}
      onClick={onClick}
    >
      {/* Row 1: ID + rubrik + status + belopp */}
      <div className="journal-row-1">
        <span className="journal-row-nr">ÄTA-{String(ata.sortOrder).padStart(3, '0')}</span>
        <span className="journal-row-rubrik">{ata.rubrik || <span style={{ color: 'var(--ink-faint)', fontWeight: 400 }}>(ingen rubrik)</span>}</span>
        <StatusBadge status={ata.status} small />
        {ata.belopp != null && (
          <span className="journal-row-belopp">{formatSEK(ata.belopp)}</span>
        )}
        <ChevronRight size={14} style={{ color: 'var(--ink-faint)', flexShrink: 0 }} />
      </div>

      {/* Row 2: beskrivning */}
      {ata.beskrivning && (
        <div className="journal-row-2">{ata.beskrivning}</div>
      )}

      {/* Row 3: meta + chips */}
      <div className="journal-row-3">
        <MetaPair label="Orsak" value={ata.orsak} />
        <MetaPair label="Tid" value={ata.paverkanTid} />
        <MetaPair label="Ansvarig" value={ata.ansvarig} />
        <div className="spacer" />
        <MissingChips
          saknarU={!ata.harUnderrattelse}
          saknarSpec={!ata.harSpec && ata.status !== 'upplagd'}
          invanarSvar={ata.bestallarSvar === 'Inväntar besked'}
        />
      </div>

      {/* Economy strip: fakturerat vs kvar att fakturera */}
      {hasEconomy && (
        <div className="fakt-strip">
          <MetaPair label="Prissatt" value={ata.belopp != null ? formatSEK(ata.belopp) : null} />
          <MetaPair label="Fakturerat" value={j?.faktureratBelopp != null ? formatSEK(j.faktureratBelopp) : null} />
          {j?.faktureratDatum && <MetaPair label="Datum" value={j.faktureratDatum} />}
          <span className="journal-meta-item">
            <span className="journal-meta-key">Kvar att fakturera</span>
            <span className="journal-meta-val" style={{ color: kvar > 0 ? 'var(--accent)' : 'var(--ink-faint)' }}>
              {formatSEK(kvar)}
            </span>
          </span>
        </div>
      )}
    </div>
  )
}

function MetaPair({ label, value }: { label: string; value?: string | number | null }) {
  if (value == null || value === '') return null
  return (
    <span className="journal-meta-item">
      <span className="journal-meta-key">{label}</span>
      <span className="journal-meta-val">{value}</span>
    </span>
  )
}
